<template>
    <v-container class="pa-5" fluid>
        <page-title>Lista zamówień niestandardowych</page-title>

        <v-row align="center">
            <v-col cols="12" offset-lg="4" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="isPlannedSendDateTodaySelected"
                                label="Wysyłka na dziś"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="filters.productionNumber"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('productionNumber')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="orders"
                        :loading="ordersLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="ordersData"
                        @pagination="updatePagination"
                    >
                        <template #item.productionNumber="{item}">
                            {{ item.productionNumber }}
                        </template>
                        <template #item.firstProductName="{item}">
                            <template v-if="item.firstProductName !== 'N/D'">
                                {{ item.firstProductName }}
                                <v-chip
                                    v-if="item.numberOfProducts > 1"
                                    class="ma-1"
                                    small
                                    color="warning"
                                >
                                    +1
                                </v-chip>
                            </template>
                            <template v-else>
                                {{ item.shopProductName }}
                            </template>
                        </template>
                        <template #item.customerName="{item}">
                            {{ parseEmptyValue(item.customer && item.customer.name) }}
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.plannedSendDate="{item}">
                            {{ formatDate(item.plannedSendDate) }}
                        </template>
                        <template #item.amount="{item}">
                            <template v-if="item.firstProductAmount">
                                {{ item.firstProductAmount }}
                            </template>
                            <template v-else>
                                {{ item.quantity }}
                            </template>
                        </template>
                        <template #item.status="{item}">
                            {{ item.status.name }}

                            <v-tooltip
                                v-if="item.overdue"
                                :open-delay="tooltipDelay"
                                top
                            >
                                <template #activator="{on}">
                                    <v-icon color="warning" small v-on="on">
                                        mdi-clock-outline
                                    </v-icon>
                                </template>
                                <span>Opóźnione</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'

import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import parse from '@/mixins/parse'

import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'NonStandardOrders',
    components: {
        PageTitle
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        dateFormatter,
        dataTableClassSetter,
        parse
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('orders/clearNonStandardOrdersFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            isPlannedSendDateTodaySelected: false,
            currentOrder: {},
            headers: [
                {
                    text: 'Numer zamówienia',
                    value: 'productionNumber',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Nazwa produktu',
                    value: 'firstProductName',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Klient',
                    value: 'customerName',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Data zamówienia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Planowana data wysyłki',
                    value: 'plannedSendDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false,
                    align: 'center'
                }
            ]
        }
    },
    computed: {
        orders() {
            return this.$store.state.orders.order.orders
        },
        ordersLoading() {
            return this.$store.state.orders.order.ordersLoading
        },
        savedFilters() {
            return this.$store.state.orders.order.nonStandardOrdersFilters
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    mounted() {
        this.getOrders()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedOrderId) {
                return 'isSelected'
            }
        },
        getOrders() {
            this.dateRangeHandle()
            this.$store
                .dispatch('orders/getOrders', {
                    paginate: this.paginate(),
                    query: `${this.filterQuery}`
                })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        dateRangeHandle() {
            if (this.isPlannedSendDateTodaySelected) {
                this.filters.plannedSendDate__gte = this.todayMinDate
                this.filters.plannedSendDate__lte = this.todayMaxDate
            } else {
                this.filters.plannedSendDate__gte = ''
                this.filters.plannedSendDate__lte = ''
            }
        },
        changeFilter() {
            this.$store.dispatch(
                'orders/setNonStandardOrdersFilters',
                this.filters
            )
            this.resetPagination()
            this.getOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getOrders()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
